// /opt/projects/saas_odonto/frontend/src/services/clinicorpApi.ts

import  api  from './api.ts'; // Importa a instância axios configurada de api.ts
import { AxiosError } from 'axios'; // Para checagem de tipo de erro se necessário

// --- Interfaces ---

/**
 * Interface para dados básicos da integração retornados por GET /calendar/clinicorp/{id}
 * Contém principalmente as credenciais, sem os detalhes (business/dentist IDs).
 */
// Em src/services/clinicorpApi.ts
export interface ClinicorpIntegrationData {
  username: string | null;
  password?: string | null;
  code_link: string | null;
  subscriber_id: string | null;
  business_id?: number | null;       // <-- ADICIONAR/VERIFICAR
  dentist_person_id?: number | null; // <-- ADICIONAR/VERIFICAR
  ignore_same_name_on_patient_create: boolean;
  can_manage_patient_create_policy: boolean;
  message?: string;
}

export interface ClinicorpPatientCreatePolicy {
  ignore_same_name_on_patient_create: boolean;
}

export interface ClinicorpPendingPatientBinding {
  binding_id: number;
  lead_id: number | null;
  requested_name: string;
  phone_masked: string;
  status: 'creating' | 'unknown';
  created_at: string;
  last_error_code: string | null;
}

export interface ClinicorpPendingPatientBindingsPage {
  items: ClinicorpPendingPatientBinding[];
  next_cursor: number | null;
}

export interface ClinicorpReconciledPatientBinding {
  binding_id: number;
  patient_id: number;
  status: 'linked';
}

/**
 * Interface para o payload ao salvar/atualizar as credenciais via PUT /calendar/clinicorp/{id}
 */
export interface ClinicorpIntegrationPayload {
  username?: string;
  password?: string; // Enviado apenas se o usuário digitar uma nova senha
  code_link?: string;
  subscriber_id?: string;
}

/**
 * Interface para um item na lista de Clínicas (Businesses) retornada pelo backend.
 */
export interface ClinicorpBusinessItem {
  id: number;
  Name: string; // Campo 'Name' retornado pela API Clinicorp
}

/**
 * Interface para um item na lista de Usuários/Profissionais retornada pelo backend.
 */
export interface ClinicorpUserItem {
  id: number;
  name: string; // <-- Alterado de FullName para name
}

/**
 * Interface para a resposta da rota GET /selectable_details.
 */
export interface SelectableDetailsResponse {
  businesses: ClinicorpBusinessItem[];
  users: ClinicorpUserItem[];
  message?: string | null; // Mensagens de aviso (ex: falha parcial na busca)
}

/**
 * Interface para o payload da rota POST /save_selected_details.
 */
export interface SelectedDetailsPayload {
  business_id: number;
  dentist_person_id: number;
}

/**
 * Interface para uma resposta genérica de sucesso.
 */
export interface SuccessResponse {
  message: string;
  webhook?: ClinicorpWebhookState | null;
}

export type ClinicorpWebhookStatus =
  | 'not_configured'
  | 'awaiting_event'
  | 'captured'
  | 'revoked';

export interface ClinicorpWebhookState {
  status: ClinicorpWebhookStatus;
  token_preview?: string | null;
  webhook_url?: string | null;
  last_received_at?: string | null;
  last_event_type?: string | null;
  revoked_at?: string | null;
}

export interface ClinicorpSyncPreflightState {
  status: 'ready' | 'degraded' | 'blocked';
  checks: {
    credentials: boolean;
    evaluation_agenda: boolean;
    timezone: boolean;
    webhook: boolean;
    confirmation_sequence: boolean;
  };
  blocking_checks: string[];
  timezone?: string | null;
  business_id?: number | null;
  dentist_person_id?: number | null;
  webhook_status: ClinicorpWebhookStatus;
  last_webhook_at?: string | null;
  confirmation_status: 'configured' | 'not_configured';
  quarantined_events: number;
  failed_events: number;
  unresolved_operations: number;
}

// --- Helper de Erro (Simplificado) ---
// Você pode usar um handler de erro mais sofisticado ou o global do api.ts
const handleClinicorpApiError = (error: unknown, defaultMessage: string): never => {
    console.error('[ClinicorpAPI Error]', defaultMessage);
     if (error instanceof AxiosError && error.response?.data?.detail) {
         const detail = error.response.data.detail;
         throw new Error(
           typeof detail === 'string'
             ? detail
             : detail.message || defaultMessage
         );
     }
     if (error instanceof AxiosError && error.response?.data?.message) {
         // Erro com campo 'message' (ex: vindo da rota de save)
         throw new Error(error.response.data.message);
     }
     // Fallback para erro genérico
    throw new Error(defaultMessage || 'Erro na operação com a API Clinicorp.');
};


// --- Funções API ---

/**
 * Busca a configuração BÁSICA (credenciais) da integração Clinicorp.
 * GET /api/integrations/calendar/clinicorp/{clinicId}
 * (Assume que clinicId é lido do localStorage internamente pela função ou passado)
 */
export async function getClinicorpIntegration(): Promise<ClinicorpIntegrationData | null> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
      console.error("[getClinicorpIntegration] clinic_id não encontrado no localStorage.");
      throw new Error("ID da clínica não encontrado para buscar integração.");
  }

  try {
    const resp = await api.get<ClinicorpIntegrationData>(
        `/api/integrations/calendar/clinicorp/${clinicId}`,
        { // Permite tratar 404 como "não encontrado" sem lançar erro
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        }
    );
    if (resp.status === 404) {
        return null; // Indica que não há integração configurada
    }
    // Retorna os dados básicos (username, subscriber_id, etc.)
    return resp.data;
  } catch (error) {
    // Trata outros erros (500, rede, etc.)
    handleClinicorpApiError(error, 'Erro ao obter configuração da integração Clinicorp');
    throw error; // Re-lança para o componente tratar
  }
}

/**
 * Salva (Cria ou Atualiza via PUT) as credenciais da integração Clinicorp.
 * PUT /api/integrations/calendar/clinicorp/{clinicId}
 */
export async function saveClinicorpIntegration(payload: ClinicorpIntegrationPayload): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
      console.error("[saveClinicorpIntegration] clinic_id não encontrado no localStorage.");
      throw new Error("ID da clínica não encontrado para salvar integração.");
  }

  try {
    // A rota PUT no backend lida com criação ou atualização
    const resp = await api.put<{ message: string }>(`/api/integrations/calendar/clinicorp/${clinicId}`, payload);
    // Retorna a mensagem de sucesso do backend
    return resp.data.message || 'Credenciais Clinicorp salvas com sucesso!';
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao salvar credenciais da integração Clinicorp');
    throw error;
  }
}

export async function updateClinicorpPatientCreatePolicy(
  clinicId: number,
  policy: ClinicorpPatientCreatePolicy
): Promise<ClinicorpPatientCreatePolicy> {
  if (!clinicId) {
    throw new Error('ID da clínica não encontrado para atualizar a regra de cadastro.');
  }

  try {
    const response = await api.patch<ClinicorpPatientCreatePolicy>(
      `/api/integrations/calendar/clinicorp/${clinicId}/patient-create-policy`,
      policy
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Não foi possível salvar a regra de cadastro de pacientes.');
    throw error;
  }
}

export async function getClinicorpPendingPatientBindings(
  clinicId: number,
  cursor = 0
): Promise<ClinicorpPendingPatientBindingsPage> {
  if (!clinicId) {
    throw new Error('ID da clínica não encontrado para consultar pendências.');
  }

  try {
    const response = await api.get<ClinicorpPendingPatientBindingsPage>(
      `/api/integrations/calendar/clinicorp/${clinicId}/patient-bindings/pending`,
      { params: { cursor } }
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Não foi possível consultar as pendências de pacientes.');
    throw error;
  }
}

export async function reconcileClinicorpPatientBinding(
  clinicId: number,
  bindingId: number,
  patientId: number
): Promise<ClinicorpReconciledPatientBinding> {
  if (!clinicId || !bindingId || !Number.isSafeInteger(patientId) || patientId <= 0) {
    throw new Error('Informe um PatientId válido para conciliar a pendência.');
  }

  try {
    const response = await api.post<ClinicorpReconciledPatientBinding>(
      `/api/integrations/calendar/clinicorp/${clinicId}/patient-bindings/${bindingId}/reconcile`,
      { patient_id: patientId }
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Não foi possível conciliar o paciente no Clinicorp.');
    throw error;
  }
}

/**
 * Deleta a configuração da integração Clinicorp (credenciais e detalhes via cascade).
 * DELETE /api/integrations/calendar/clinicorp/{clinicId}
 */
export async function deleteClinicorpIntegration(): Promise<string> {
  const clinicId = localStorage.getItem('clinic_id');
  if (!clinicId) {
      console.error("[deleteClinicorpIntegration] clinic_id não encontrado no localStorage.");
      throw new Error("ID da clínica não encontrado para deletar integração.");
  }

  try {
    const resp = await api.delete<{ message: string }>(`/api/integrations/calendar/clinicorp/${clinicId}`);
    return resp.data.message || 'Integração Clinicorp removida com sucesso!';
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao deletar integração Clinicorp');
    throw error;
  }
}

/**
 * Busca as listas de Clínicas (Businesses) e Usuários (Persons) selecionáveis do Clinicorp.
 * Chamada após salvar as credenciais.
 * GET /api/integrations/clinicorp/{clinic_id}/selectable_details
 */
export async function getSelectableClinicorpDetails(clinicId: number): Promise<SelectableDetailsResponse> {
  if (!clinicId) {
    throw new Error("Clinic ID é necessário para buscar detalhes selecionáveis.");
  }
  try {
    const resp = await api.get<SelectableDetailsResponse>(
      `/api/integrations/clinicorp/${clinicId}/selectable_details`
    );
    // Retorna o objeto { businesses: [...], users: [...], message?: "..." }
    return resp.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao buscar opções de clínicas e profissionais do Clinicorp.');
    throw error;
  }
}

/**
 * Salva os IDs de Business e Dentist Person selecionados pelo usuário no backend.
 * POST /api/integrations/clinicorp/{clinic_id}/save_selected_details
 */
export async function saveSelectedClinicorpDetails(
    clinicId: number,
    payload: SelectedDetailsPayload // { business_id: number, dentist_person_id: number }
): Promise<SuccessResponse> {
  if (!clinicId) {
    throw new Error("Clinic ID é necessário para salvar detalhes selecionados.");
  }
  if (!payload || payload.business_id == null || payload.dentist_person_id == null) {
      // Verifica se os IDs são válidos (não null/undefined)
      throw new Error("É necessário fornecer o ID da clínica e do profissional selecionados.");
  }

  try {
    const resp = await api.post<SuccessResponse>(
      `/api/integrations/clinicorp/${clinicId}/save_selected_details`,
      payload // Envia os IDs selecionados no corpo
    );
    return resp.data; // Retorna { message: "..." }
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao salvar detalhes selecionados da integração Clinicorp.');
    throw error;
  }
}

export async function getClinicorpWebhook(
  clinicId: number
): Promise<ClinicorpWebhookState> {
  try {
    const response = await api.get<ClinicorpWebhookState>(
      `/api/integrations/clinicorp/${clinicId}/webhook`
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao consultar o webhook do Clinicorp.');
    throw error;
  }
}

export async function getClinicorpSyncPreflight(
  clinicId: number
): Promise<ClinicorpSyncPreflightState> {
  try {
    const response = await api.get<ClinicorpSyncPreflightState>(
      `/api/integrations/clinicorp/${clinicId}/preflight`
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao validar a sincronização do Clinicorp.');
    throw error;
  }
}

export async function provisionClinicorpWebhook(
  clinicId: number
): Promise<ClinicorpWebhookState> {
  try {
    const response = await api.post<ClinicorpWebhookState>(
      `/api/integrations/clinicorp/${clinicId}/webhook/provision`
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao gerar o webhook do Clinicorp.');
    throw error;
  }
}

export async function rotateClinicorpWebhook(
  clinicId: number
): Promise<ClinicorpWebhookState> {
  try {
    const response = await api.post<ClinicorpWebhookState>(
      `/api/integrations/clinicorp/${clinicId}/webhook/rotate`
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao gerar um novo link de webhook.');
    throw error;
  }
}

export async function revokeClinicorpWebhook(
  clinicId: number
): Promise<ClinicorpWebhookState> {
  try {
    const response = await api.delete<ClinicorpWebhookState>(
      `/api/integrations/clinicorp/${clinicId}/webhook`
    );
    return response.data;
  } catch (error) {
    handleClinicorpApiError(error, 'Erro ao revogar o webhook do Clinicorp.');
    throw error;
  }
}
